import { last } from 'lodash';
import React, { Suspense, useContext } from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { UserContext, AllocationAsOfDateDropdown, BlockIdContext, useBlockId, useMetricsError } from 'venn-components';
import {
  type BlockAsOfDateSettingIndices,
  blockLimitedRequestSubjects,
  rangeDebugQuery,
  type StudioRequestSubject,
  useBlockAsOfDateSetting,
  blockSettings,
} from 'venn-state';
import { GetColor, Icon, ShimmerBlock, Tooltip } from 'venn-ui-kit';
import {
  type AsOfDateSetting,
  assertNotNil,
  getSubjectFromRequestSubject,
  isHistoricalSubject,
  analyticsService,
  serializeAsOfDateSetting,
} from 'venn-utils';
import { FlexHeader } from '../../../../shared';

const AllocationAsOfDateInputControl = ({
  subject,
  asOfDate,
  setAsOfDate,
}: {
  subject: StudioRequestSubject;
  asOfDate: AsOfDateSetting;
  setAsOfDate: (asOfDate: AsOfDateSetting) => void;
}) => {
  const { hasPermission } = useContext(UserContext);
  const blockId = useBlockId();
  const maybeError = useMetricsError({ blockId });

  const rangeDebug = useRecoilValue(rangeDebugQuery(getSubjectFromRequestSubject(subject)));

  const lastUploadedAllocationsDate = last(rangeDebug?.historicalPortfolioMetadata?.allSortedRebalancingDates);
  const readonly = !hasPermission('STUDIO_EDIT_DATES') || !hasPermission('STUDIO_EDIT');

  const allocationDates = assertNotNil(rangeDebug?.historicalPortfolioMetadata?.allSortedAnalysisAllocationDates);

  return (
    <AllocationAsOfDateDropdown
      allocationsAsOfDate={asOfDate}
      lastUploadedAllocationsDate={assertNotNil(lastUploadedAllocationsDate)}
      currentDate={assertNotNil(rangeDebug?.maxEndDate)}
      allocationDates={allocationDates}
      setAllocationsAsOfDate={setAsOfDate}
      frequency={rangeDebug?.maxFrequency ?? 'UNKNOWN'}
      disabled={readonly || !!maybeError}
      triggerSize="normal"
    />
  );
};

export const AllocationAsOfDateInput = ({ selectedBlockId }: { selectedBlockId: string }) => {
  const subjects = useRecoilValue(blockLimitedRequestSubjects(selectedBlockId)).slice(0, 2);
  const { blockAsOfDate, setAsOfDateByIndex } = useBlockAsOfDateSetting(selectedBlockId);
  const blockSetting = useRecoilValue(blockSettings(selectedBlockId));
  const blockType = blockSetting.customBlockType;

  return (
    // we use selectedBlockId as a key to force component reinstantiation when block changes
    <div data-testid="qa-block-date-range" key={selectedBlockId}>
      <FlexHeader style={{ marginBottom: '12px' }}>
        Allocation As of Date
        <Tooltip
          content={
            <TooltipContent>Add a portfolio with historical allocations in order to set an as of date.</TooltipContent>
          }
          usePortal
        >
          <InfoIcon type="info-circle" prefix="far" />
        </Tooltip>
      </FlexHeader>
      <div className="flex flex-col gap-y-[8px]">
        {subjects.map((subject, index) => {
          if (!subject || !isHistoricalSubject(subject)) {
            return null;
          }
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={`${subject.id}-${index}`} data-testid={`allocation-as-of-date-input-${index}`}>
              <div className="flex flex-col gap-y-[4px]">
                <div className="text-[12px]">{subject.name}</div>
                <Suspense fallback={<ShimmerBlock height={32} width="210px" />}>
                  <BlockIdContext.Provider value={selectedBlockId}>
                    <AllocationAsOfDateInputControl
                      subject={subject}
                      asOfDate={assertNotNil(blockAsOfDate[index])}
                      setAsOfDate={(asOfDate) => {
                        analyticsService.dropdownItemSSelected({
                          label: `${blockType}_AS_OF_DATE`,
                          type: 'primary dropdown',
                          selectType: 'single-select',
                          selection: serializeAsOfDateSetting(asOfDate),
                        });
                        setAsOfDateByIndex(index as BlockAsOfDateSettingIndices, asOfDate);
                      }}
                    />
                  </BlockIdContext.Provider>
                </Suspense>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const InfoIcon = styled(Icon)`
  color: ${GetColor.GreyScale.Grey80};
  margin-left: 4px;
  font-size: 14px;
`;

const TooltipContent = styled.div`
  width: 236px;
  padding: 5px;
`;
